import { useQueryClient } from "@tanstack/react-query";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { SizedText } from "../../styles/styles";
import { Article } from "../../types/article";
import { useTasksByArticle } from "../../hooks/useTasksByArticle";
import { useStoreId, useUID } from "../../../../core/auth/useLoggedInUser";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaPrompt } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaPrompt";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { setOngoingTasksToClosed } from "../../tools";
import { Trans, useTranslation } from "@coworker/locales";
import { useDeleteArticlesByIds } from "../../hooks/useDeleteArticlesByIds";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import { updateMainAndAlternativeProducts } from "../../hooks/useDeleteArticles.helper";
import { deleteArticleById } from "../../services/articles.service";
import { Reasons } from "../../constants";
import { createAutoGeneratedTaskAsCompleted } from "../../Molecules/ArticleListItemActionRow.helper";
import { useCreateTask } from "../../../../hooks/TaskActions/useCreateTask";
import { useMediaById } from "../../hooks/useMediaById";
import useFlag, { FLAGS } from "../../../../hooks/useFlag";
import { useMediaTaskTypeIds } from "@coworker/apprestructured/src/task-management/services/useMediaTaskTypeIds";
import { removeTaskDetailsReference } from "@coworker/apprestructured/src/task-management/services/tasks.service";
import { getMediaItemsWithTasksQueryKey } from "@coworker/apprestructured/src/task-management/services/queryKeyHelper";

interface DeleteArticleConfirmModalProps {
  article: Article;
  onModalOpenChange: (modalOpen: boolean) => void;
  postDeleteFunction?: () => Promise<void>;
  noInvalidateQueries?: boolean;
}

export const DeleteArticleConfirmModal = ({
  article,
  onModalOpenChange,
  postDeleteFunction,
  noInvalidateQueries = false,
}: DeleteArticleConfirmModalProps) => {
  const queryClient = useQueryClient();
  const { showToast } = useToastNotification();
  const { t } = useTranslation();
  const uid = useUID();
  const storeId = useStoreId();
  const { call: createTask } = useCreateTask();

  const tasks = useTasksByArticle(storeId, article.productArticleId);
  const deleteArticles = useDeleteArticlesByIds(article.mediaId);
  const { data: mediaResponse } = useMediaById(article?.mediaId || "");
  const enableMediaV2Tasks = useFlag(FLAGS.ENABLE_MEDIA_V2_TASKS);
  const mediaTaskTypeIds = useMediaTaskTypeIds();

  const createAutoGeneratedTaskWithReason = async (
    reason: Reasons,
    productArticleId: string
  ) => {
    await createAutoGeneratedTaskAsCompleted(
      uid,
      reason,
      article.mediaId,
      mediaResponse?.media.name ?? "",
      storeId,
      article,
      productArticleId,
      t("taskIsAutoCompletedNoteString"),
      createTask
    );
  };

  const handleDeleteArticle = async () => {
    const tasksToBeRemoved = {
      ...tasks,
      taskLinks: tasks?.taskLinks?.filter((tl) => tl.articleId === article.id),
      tasks: tasks?.tasks?.filter((t) => t.article_id === article.id),
    };
    await setOngoingTasksToClosed(
      article.mediaId,
      t("taskClosedBecauseRemovedArticleString"),
      tasksToBeRemoved,
      uid
    );
    await createAutoGeneratedTaskWithReason(
      Reasons.REMOVE_PRODUCT,
      article.productArticleId
    );
    if (enableMediaV2Tasks) {
      await removeTaskDetailsReference(
        {
          media_id: article.mediaId,
          group_id: article.groupId,
          article_id: article.id,
          item_no: article.productArticleId,
        },
        mediaTaskTypeIds
      );
      await queryClient.invalidateQueries({
        queryKey: ["media-task-counts-v2"],
      });
      await queryClient.invalidateQueries({
        queryKey: getMediaItemsWithTasksQueryKey(article.mediaId),
      });
    }
    try {
      if (noInvalidateQueries) {
        await updateMainAndAlternativeProducts(article.mediaId, [article.id]);
        await deleteArticleById(article.id, queryClient);
      } else {
        await deleteArticles.mutateAsync([article.id]);
      }
      if (postDeleteFunction) await postDeleteFunction();
    } catch {
      showToast(t("searchErrorString"));
    }
    onModalOpenChange(false);
    showToast(t("articleDeletedString"));
  };

  let tasksWillBeClosed;
  if (article.groupName) {
    tasksWillBeClosed = <Trans>deleteArticleInGroupSetTasksClosedString</Trans>;
  } else {
    tasksWillBeClosed = (
      <Trans values={{ mediaName: t("mediaMaintenanceString") }}>
        deleteArticleSetTasksClosedString
      </Trans>
    );
  }

  return (
    <>
      <FixaModal visible={true} handleCloseBtn={() => onModalOpenChange(false)}>
        <FixaPrompt
          title={""}
          titleId={`delete-${article.id}`}
          header={
            <FixaModalHeader
              title={t("deleteNameString", { name: article.itemName })}
              ariaCloseTxt="Close prompt."
            />
          }
          footer={
            <FixaModalFooter>
              <FixaButton
                text={t("cancelString")}
                type="secondary"
                onClick={() => onModalOpenChange(false)}
              />
              <FixaButton
                text={t("deleteString")}
                type="danger"
                onClick={handleDeleteArticle}
              />
            </FixaModalFooter>
          }
        >
          <>
            <SizedText $fontSize={16} $bold={true}>
              <Trans values={{ item: article.itemName }}>
                surelyDeleteString
              </Trans>
            </SizedText>
            <SizedText $fontSize={14} $bold={false}>
              {tasksWillBeClosed}
              &nbsp;
              <Trans>canNotBeUndoneString</Trans>
            </SizedText>
          </>
        </FixaPrompt>
      </FixaModal>
    </>
  );
};
